'use client';
import { FormEvent, useContext, useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import { useGoogleLogin } from '@react-oauth/google';
import axios from 'axios';
import { useTranslations } from 'next-intl';
import Head from 'next/head';
import { useRouter } from 'next/navigation';
import * as Yup from 'yup';

import {
  AuthDivider,
  AuthInput,
  AuthLayout,
  AuthPanel,
  AuthSubmitButton
} from '../../../components/Auth';
import GoogleOAuthButton from '../../../components/GoogleOAuthButton';
import { amplitudeInstance } from '../../../config/amplitude';
import env from '../../../config/env';
import constants from '../../../constants/constants.json';
import AuthContext from '../../../context/AuthContext';
import {
  AUTH_REDIRECT_FALLBACK_PATH,
  buildAuthPageLinkPath,
  sanitizeAuthRedirectTarget
} from '../../../helpers/authRedirect';
import { generateHrefLang } from '../../../helpers/generateHrefLang';
import { Link } from '../../../i18n/navigation';

interface FormValidationError {
  email: string;
  password: string;
}

interface PropTypes {
  redirectTarget?: string;
}

export default function Login({
  redirectTarget: rawRedirectTarget
}: PropTypes) {
  const t = useTranslations();

  const auth = useContext(AuthContext);
  const router = useRouter();
  const redirectTarget = sanitizeAuthRedirectTarget(
    rawRedirectTarget,
    constants.I18.SUPPORTED_LOCALES
  );
  const shouldPreserveRedirectTarget =
    !!rawRedirectTarget && redirectTarget !== AUTH_REDIRECT_FALLBACK_PATH;
  const signupPath = buildAuthPageLinkPath(
    '/signup',
    redirectTarget,
    shouldPreserveRedirectTarget
  );

  const [shouldRedirect, setShouldRedirect] = useState(true);
  const [warning, setWarning] = useState<FormValidationError | null>(null);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const googleLogin = useGoogleLogin({
    onSuccess: codeResponse => {
      handleGoogleSignIn({ accessToken: codeResponse.access_token });
    },
    onError: () => showGoogleOAuthError(),
    onNonOAuthError: error => {
      if (error.type !== 'popup_closed') showGoogleOAuthError();
    },
    flow: 'implicit'
  });

  function showGoogleOAuthError() {
    toast(t('sign_in.errors.google_oauth_failed'), {
      position: 'bottom-center',
      type: 'error'
    });
  }

  useEffect(() => {
    if (!auth.isLoading && auth.isLoggedIn && shouldRedirect) {
      router.replace(redirectTarget);
    }
  }, [auth.isLoading, auth.isLoggedIn, redirectTarget, router, shouldRedirect]);

  useEffect(() => {
    amplitudeInstance.logEvent('Login Page View');
  }, []);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    try {
      const schema = Yup.object().shape({
        email: Yup.string()
          .email(t('sign_in.errors.invalid_email_format'))
          .required(t('sign_in.errors.required_email')),
        password: Yup.string()
          .min(4)
          .max(60)
          .required(t('sign_in.errors.required_password'))
      });

      const isValid = await schema.validate(
        { email, password },
        { abortEarly: false }
      );

      if (isValid) {
        setWarning(null);
        await handleSignIn();
      }
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        interface ValidationError extends FormValidationError {
          [key: string]: string;
        }

        const validationErrors: ValidationError = {
          email: '',
          password: ''
        };

        error.inner.forEach(validationError => {
          validationErrors[validationError.path as string] =
            validationError.message;
        });

        setWarning(validationErrors);
      }
    }
  }

  async function handleGoogleSignIn({ accessToken }: { accessToken: string }) {
    try {
      setGoogleLoading(true);
      const response = await axios.post('/api/oauth', {
        code: accessToken,
        type: 'sign_in',
        provider: 'google',
        client: 'web'
      });

      if (response.status === 200) {
        amplitudeInstance.setUserId(response.data.data._id);
        amplitudeInstance.logEvent('Google Login');

        window.location.href = redirectTarget;
      }
    } catch (err: any) {
      const status = err?.response?.status;

      if (status === 401) {
        toast(t('sign_in.errors.account_not_found'), {
          position: 'bottom-center',
          type: 'error'
        });
        return;
      }

      if (status === 403) {
        toast(t('sign_in.errors.account_suspended'), {
          position: 'bottom-center',
          type: 'error'
        });
        return;
      }

      if (status === 429) {
        toast(t('sign_in.errors.rate_limit'), {
          position: 'bottom-center',
          type: 'error'
        });
        return;
      }

      showGoogleOAuthError();
    } finally {
      setGoogleLoading(false);
    }
  }

  async function handleSignIn() {
    try {
      setLoading(true);
      const response = await axios.post('/api/signin', {
        email,
        password,
        client: 'web'
      });

      setLoading(false);
      if (response.status === 200) {
        amplitudeInstance.setUserId(response.data.data._id);
        amplitudeInstance.logEvent('Login');

        setShouldRedirect(false);

        window.location.href = redirectTarget;
      }
    } catch (err: any) {
      setLoading(false);
      const status = err?.response?.status;

      if (status === 401) {
        toast(t('sign_in.errors.invalid_email_or_password'), {
          position: 'bottom-center',
          type: 'error'
        });
      }

      if (status === 429) {
        toast(t('sign_in.errors.rate_limit'), {
          position: 'bottom-center',
          type: 'error'
        });
      }

      if (status === 500) {
        toast(t('sign_in.errors.internal_server_error'), {
          position: 'bottom-center',
          type: 'error'
        });
      }
    }
  }

  return (
    <>
      <Head>
        <title>{`${t('sign_in.head_title')}`}</title>
        <meta property="og:title" content={`${t('sign_in.head_title')}`} />
        <meta
          key="description"
          name="description"
          content={t('sign_in.description')}
        />
        <meta
          key="og:description"
          name="og:description"
          content={t('sign_in.description')}
        />

        <meta
          key="og:image"
          name="og:image"
          content={'https://trakio.app/assets/og-preview.jpg'}
        />

        <link
          rel="alternate"
          hrefLang="x-default"
          href={`${env.WEBAPP_URL}/login`}
        />
        <link rel="canonical" href={`${env.WEBAPP_URL}/login`} />

        {Object.values(constants.CONTENT_REGIONS).map(locale => {
          return (
            <link
              key={locale}
              rel="alternate"
              hrefLang={generateHrefLang(locale)}
              href={`${env.WEBAPP_URL}/login?region=${locale.toLowerCase()}`}
            />
          );
        })}
      </Head>

      <AuthLayout
        sideTitle={t('landing.title')}
        sideDescription={t('landing.subtitle')}
        highlights={[
          t('landing.get_the_app.title'),
          t('landing.find_where_to_watch.title'),
          t('landing.import_trakt_data.title')
        ]}
      >
        <AuthPanel
          title={t('sign_in.title')}
          description={t('sign_in.description')}
          footer={
            <>
              {t('sign_in.sign_up_prompt')}{' '}
              <Link
                href={signupPath}
                className="font-bold text-white hover:text-red-600"
              >
                {t('sign_in.sign_up_button')}
              </Link>
            </>
          }
        >
          <form className="flex flex-col" onSubmit={handleSubmit}>
            <AuthInput
              id="email"
              name="email"
              type="email"
              label={t('sign_in.email')}
              placeholder={t('sign_in.email_placeholder')}
              value={email}
              autoComplete="email"
              error={warning?.email}
              onChange={e => setEmail(e.target.value)}
            />

            <AuthInput
              id="password"
              name="password"
              type="password"
              label={t('sign_in.password')}
              placeholder={t('sign_in.password_placeholder')}
              required
              value={password}
              autoComplete="current-password"
              containerClassName="mt-5"
              error={warning?.password}
              onChange={e => setPassword(e.target.value)}
            />

            <Link
              className="mt-3 self-end text-sm font-semibold text-gray-100 hover:text-white"
              href="/request-password-reset"
            >
              {t('sign_in.forgot_password_button')}
            </Link>

            <div className="mt-6">
              <AuthSubmitButton
                label={t('sign_in.submit_button')}
                loading={loading}
              />
            </div>
          </form>

          <AuthDivider />

          <GoogleOAuthButton
            text={t('sign_in.sign_in_with_google')}
            onClick={() => googleLogin()}
            disabled={googleLoading}
          />
        </AuthPanel>
      </AuthLayout>
    </>
  );
}
